import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PlayCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useAdPlayMonthlyStats } from '@/hooks/useAdPlayMonthlyStats'
import { useAdScheduleTemplateStats } from '@/hooks/useAdScheduleTemplateStats'

export default function AdPlaybackStats() {
  const {
    currentMonthTotal,
    lastMonthTotal,
    growthRate,
    isLoading: monthlyLoading,
  } = useAdPlayMonthlyStats()
  const { totalClinics, totalDevices, isLoading: statsLoading } = useAdScheduleTemplateStats()

  const isLoading = monthlyLoading || statsLoading

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    )
  }

  // 格式化數字
  const formatNumber = (num: number) => {
    return num.toLocaleString('zh-TW')
  }

  // 決定趨勢圖標和顏色
  const getTrendIcon = () => {
    if (growthRate > 0) {
      return <TrendingUp className="h-4 w-4 text-green-500" />
    } else if (growthRate < 0) {
      return <TrendingDown className="h-4 w-4 text-red-500" />
    }
    return <Minus className="h-4 w-4 text-gray-400" />
  }

  const getTrendColor = () => {
    if (growthRate > 0) return 'text-green-600'
    if (growthRate < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  return (
    <Card className="h-full bg-gradient-to-br from-blue-50 to-blue-100/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-blue-600" />
          廣告播放統計
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 本月播放次數 */}
        <div>
          <p className="text-xs text-gray-600">本月播放次數</p>
          <p className="text-xl font-bold text-gray-900">{formatNumber(currentMonthTotal)}</p>
        </div>

        {/* 上月播放次數 */}
        <div>
          <p className="text-xs text-gray-600">上月播放次數</p>
          <p className="text-lg font-semibold text-gray-700">{formatNumber(lastMonthTotal)}</p>
        </div>

        {/* 成長率 */}
        <div className="flex items-center justify-between p-2 bg-white/70 rounded">
          <span className="text-xs text-gray-600">月成長率</span>
          <div className="flex items-center gap-1">
            {getTrendIcon()}
            <span className={`text-sm font-semibold ${getTrendColor()}`}>
              {Math.abs(growthRate).toFixed(1)}%
            </span>
          </div>
        </div>

        {/* 套用統計 */}
        <div className="pt-2 border-t border-blue-200/50">
          <p className="text-xs text-gray-600 mb-1">排班模版套用狀態</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white/50 p-2 rounded">
              <p className="text-gray-500">套用診所</p>
              <p className="font-semibold text-gray-900">{formatNumber(totalClinics)}</p>
            </div>
            <div className="bg-white/50 p-2 rounded">
              <p className="text-gray-500">套用設備</p>
              <p className="font-semibold text-gray-900">{formatNumber(totalDevices)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
